/**
 * v0.4 追加要件⑭: 「配下の組織」一覧の集計単位 (年度 / 四半期 / 月) の選択ロジック。
 *
 * ⚠️ 画面から切り出して純関数にしてある。Radix Select はブラウザ自動化から開けないため
 *    (§8-60 と同型のツール制約)、切替の正しさは単体テストで担保する。
 * ⚠️ **ここで月を足し直さないこと。**年度・四半期・月の集計はすべてサーバが
 *    同じ `aggregatePeriod` で計算して返している。画面側で足すと二重定義になり (§8-55)、
 *    「確定済みの月だけで対比する」規則が片方だけ変わって食い違う。
 */

export type ChildScope =
  | { kind: 'year' }
  | { kind: 'quarter'; quarter: number }
  | { kind: 'month'; month: string };

export type ScopeCells = {
  /** その期間全体の目標 (未経過分を含む。目標が揃っていない月は除く) */
  total: number | null;
  months_counted: number;
  /** 経過分の計算に使った月 ('YYYY-MM-01') */
  months: string[];
  /** 実績 ÷ 期間の目標。月次では出さない (null) */
  progress_rate: number | null;
  target: number | null;
  actual: number | null;
  achievement_rate: number | null;
  variance: number | null;
};

type ToDate = {
  months_counted: number; months: string[]; target: number | null; actual: number | null;
  achievement_rate: number | null; variance: number | null;
};
type PeriodLike = { target: number | null; progress_rate: number | null; to_date: ToDate };
type MonthLike = {
  month: string; target: number | null; actual: number | null;
  achievement_rate: number | null; variance: number | null; counted: boolean;
};

export const EMPTY_SCOPE_CELLS: ScopeCells = {
  total: null, months_counted: 0, months: [], progress_rate: null,
  target: null, actual: null, achievement_rate: null, variance: null,
};

/** セレクトの value 文字列 ⇔ ChildScope の相互変換。 */
export const scopeToValue = (scope: ChildScope): string =>
  scope.kind === 'year' ? 'year' : scope.kind === 'quarter' ? `q${scope.quarter}` : scope.month;

export const valueToScope = (value: string): ChildScope => {
  if (value === 'year') return { kind: 'year' };
  if (/^q[1-4]$/.test(value)) return { kind: 'quarter', quarter: Number(value.slice(1)) };
  return { kind: 'month', month: value };
};

/** 選択中の期間に対応する表示値を取り出す。
 *  ⚠️ 月次は MonthProgress をそのまま使う (`total` と `target` は同じ値になる — 月には
 *  「経過分」という概念が無いため。画面は月次のとき「目標 (経過分)」列を出さない)。 */
export const selectScopeCells = (
  scope: ChildScope,
  row: { year: PeriodLike; quarters: (PeriodLike & { quarter: number })[]; months: MonthLike[] },
): ScopeCells => {
  if (scope.kind === 'year') return { total: row.year.target, progress_rate: row.year.progress_rate, ...row.year.to_date };
  if (scope.kind === 'quarter') {
    const q = row.quarters.find((x) => x.quarter === scope.quarter);
    if (!q) return EMPTY_SCOPE_CELLS;
    return { total: q.target, progress_rate: q.progress_rate, ...q.to_date };
  }
  const m = row.months.find((x) => x.month === scope.month);
  if (!m) return EMPTY_SCOPE_CELLS;
  return {
    total: m.target,
    // ⚠️ 経過分に数えるかはサーバの counted をそのまま使う。ここで規則を写すと食い違う (§8-134)
    months_counted: m.counted ? 1 : 0,
    months: m.counted ? [m.month] : [],
    progress_rate: null,
    target: m.target,
    actual: m.actual,
    achievement_rate: m.achievement_rate,
    variance: m.variance,
  };
};

/** 集計単位セレクトの選択肢。⚠️ 月の並びは年度順 (サーバが返す months の順) をそのまま使う。
 *  年度開始月が4月とは限らないため、ここで 1〜12 に並べ替えてはならない。 */
export const buildScopeOptions = (
  months: string[], monthLabel: (m: string) => string,
): { value: string; label: string }[] => [
  { value: 'year', label: '年度累計' },
  ...[1, 2, 3, 4].map((q) => ({ value: `q${q}`, label: `第${q}四半期 累計` })),
  ...months.map((m) => ({ value: m, label: monthLabel(m) })),
];

const monthNum = (m: string): number => Number.parseInt(m.slice(5, 7), 10);
const yearNum = (m: string): number => Number.parseInt(m.slice(0, 4), 10);
const monthIndex = (m: string): number => yearNum(m) * 12 + monthNum(m);

/** 経過分の計算に使った月の表示 (例:「7〜8月（2か月）の実績で計算」)。
 *  ⚠️ 目標が揃っていない月が途中にあると連続しないため、連続していなければ「7・9月」と列挙する。
 *  年をまたぐときは年を付ける。入力は 'YYYY-MM-01' 形式 (§8-57)。 */
export const formatCountedMonths = (months: string[]): string => {
  if (months.length === 0) return '確定した月がまだありません';
  const sorted = [...months].sort();
  const crossYear = yearNum(sorted[0]!) !== yearNum(sorted[sorted.length - 1]!);
  // 同じ年の中では「7〜8月」「7・9月」、年をまたぐときは各月に年と「月」を付ける
  const label = (m: string): string => (crossYear ? `${yearNum(m)}年${monthNum(m)}月` : `${monthNum(m)}`);
  const suffix = crossYear ? '' : '月';
  const contiguous = sorted.every((m, i) => i === 0 || monthIndex(m) === monthIndex(sorted[i - 1]!) + 1);
  const body = (sorted.length === 1
    ? label(sorted[0]!)
    : contiguous
      ? `${label(sorted[0]!)}〜${label(sorted[sorted.length - 1]!)}`
      : sorted.map(label).join('・')) + suffix;
  return `${body}（${sorted.length}か月）の実績で計算`;
};

export type MonthTargetCard =
  | { kind: 'hidden' }
  | { kind: 'rate'; target: number; rate: number | null }
  | { kind: 'notice'; target: number; message: string };

export const MONTH_TARGET_INCOMPLETE_MESSAGE = '目標が一部未登録のため、達成率を表示していません';
export const MONTH_TARGET_UNPUBLISHED_MESSAGE = '未公開の店舗を含むため、達成率を表示していません';

/** 選択中の月の目標カードの表示内容 (D-944)。分母は /progress の該当月の目標、分子は画面の売上。
 *  ⚠️ 未公開の店舗があるかは、分子と同じゲート結果 (ダッシュボード応答の publish_gate) で判定する。
 *     /progress の actual_state は未確定の月で pending が先に立つため、当月では判定できない。 */
export const resolveMonthTargetCard = (p: {
  month: string;
  months: { month: string; target: number | null; target_incomplete: boolean }[] | undefined;
  sales: number | null | undefined;
  excludedStoreCount: number;
}): MonthTargetCard => {
  const row = p.months?.find((m) => m.month === p.month);
  if (!row || row.target === null) return { kind: 'hidden' };
  if (row.target_incomplete) return { kind: 'notice', target: row.target, message: MONTH_TARGET_INCOMPLETE_MESSAGE };
  if (p.excludedStoreCount > 0) return { kind: 'notice', target: row.target, message: MONTH_TARGET_UNPUBLISHED_MESSAGE };
  const rate = row.target > 0 && p.sales != null ? p.sales / row.target : null;
  return { kind: 'rate', target: row.target, rate };
};
